function readJsonLocalStorage(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Normalize role flags for visibility checks. `gateway-userInfo` and login
 * `userInfo` may use booleans, `role`, or both.
 *
 * @param {object | null | undefined} userInfo
 * @returns {object | null}
 */
export function normalizeChatbotUserFlags(userInfo) {
  if (!userInfo || typeof userInfo !== 'object') {
    return null;
  }
  const role = String(userInfo.role || '')
    .trim()
    .toLowerCase();
  const isAdmin = userInfo.isAdmin === true || role === 'admin';
  const isClient = userInfo.isClient === true || role === 'client';
  const isInternal =
    userInfo.isInternal === true ||
    (role === 'internal' && !isAdmin && !isClient);
  return { ...userInfo, isAdmin, isClient, isInternal };
}

/**
 * Prefer gateway-userInfo; fall back to login userInfo (same pattern as PermissionCheck).
 *
 * @returns {object | null}
 */
export function parseGatewayUserInfo() {
  return (
    normalizeChatbotUserFlags(readJsonLocalStorage('gateway-userInfo')) ||
    normalizeChatbotUserFlags(readJsonLocalStorage('userInfo'))
  );
}

function isAnyProjectMember(userInfo) {
  const flags = normalizeChatbotUserFlags(userInfo);
  if (!flags) {
    return false;
  }
  return flags.isAdmin === true || flags.isInternal === true || flags.isClient === true;
}

/**
 * @param {unknown} allowedTokens — from GET /chatbot/visibility/:moduleId
 * @param {object | null | undefined} userInfo — gateway-userInfo shape
 * @returns {boolean}
 */
export function userMatchesChatbotVisibility(allowedTokens, userInfo) {
  if (!Array.isArray(allowedTokens)) {
    return false;
  }
  const flags = normalizeChatbotUserFlags(userInfo);
  if (!flags) {
    return false;
  }
  // TEMPORARY: treat [] as admin-only until API always returns explicit audiences. Revert: use `return false` when length === 0.
  const tokens =
    allowedTokens.length === 0 ? ['admin'] : allowedTokens;
  for (const raw of tokens) {
    const t = String(raw).trim().toLowerCase();
    if (t === 'admin' && flags.isAdmin === true) {
      return true;
    }
    if (t === 'internal' && flags.isInternal === true) {
      return true;
    }
    if ((t === 'client' || t === 'external') && flags.isClient === true) {
      return true;
    }
    if (t === 'allmembers' && isAnyProjectMember(flags)) {
      return true;
    }
  }
  return false;
}
